import type { MouseEventHandler, ReactNode } from 'react'
import { IconChevronRight } from '@n26/nxd'
import { IconWithTextLink } from '@components/IconWithTextLink'
import { useCountryAwareSlugWithPrefix } from '@hooks/useCountryAwareSlugWithPrefix'
import type { CountryAwareLinkItem } from '@customTypes/generated/contentfulGraphqlAPI'
import { useOptimizelyEventTracker } from '@hooks/useOptimizelyEventTracker'
import { useLandingPageData } from '@hooks/useLandingPageData'
import { getOptimizelyTrackingEventKey } from '@helpers/getOptimizelyTrackingEventKey'

type ReadMoreProps = {
  readMore?: CountryAwareLinkItem | null
  externalLink?: string | null
  onClick?: MouseEventHandler<HTMLAnchorElement>
  fontColor?:
    | 'teal'
    | 'petrol'
    | 'wheat'
    | 'rhubarb'
    | 'neutral'
    | 'neutral-alternate'
    | 'neutral-tertiary'
    | 'petrol-alternate'
    | 'rhubarb-alternate'
    | 'teal-alternate'
    | 'wheat-alternate'
  children?: ReactNode
}

export function CountryAwareReadMore({
  readMore,
  externalLink,
  onClick,
  fontColor,
  children,
}: ReadMoreProps) {
  const landingPageData = useLandingPageData()

  const readMorePageSlug = useCountryAwareSlugWithPrefix(readMore)
  const trackOptimizelyEvent = useOptimizelyEventTracker()

  // activeLink allows
  const slugOrLink = externalLink || readMorePageSlug

  if (!slugOrLink) {
    return null
  }

  const { label } = readMore || {}

  const handleClick: MouseEventHandler<HTMLAnchorElement> = event => {
    const eventKey = getOptimizelyTrackingEventKey({
      ctaOptimizelyEvent: readMore?.optimizelyEventKey,
      productPreference: landingPageData?.productPreference,
    })

    if (eventKey) {
      trackOptimizelyEvent({ eventKey })
    }

    onClick?.(event)
  }

  return (
    <IconWithTextLink
      EndIcon={IconChevronRight}
      text={children || label}
      url={slugOrLink}
      onClick={handleClick}
      fontColor={fontColor}
    />
  )
}
